import { useEffect } from 'react';
import { dispatchSseEvent } from './sse-events/sseEventStrategy';

export default function useSseNotifications(endpoint) {
  useEffect(() => {
    let shouldReconnect = true;
    let reconnectTimeout = null;
    let retryDelay = 3000;
    let activeController = null;

    const parseSseChunk = (chunk, onEvent) => {
      const lines = chunk.split('\n');
      let event = 'message';
      const dataLines = [];

      lines.forEach((line) => {
        if (line.startsWith('event:')) {
          event = line.slice(6).trim();
        }
        if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim());
        }
      });

      if (dataLines.length > 0) {
        onEvent(event, dataLines.join('\n'));
      }
    };

    const scheduleReconnect = () => {
      if (!shouldReconnect) {
        return;
      }

      reconnectTimeout = setTimeout(() => {
        connectToSse();
      }, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 15000);
    };

    const connectToSse = async () => {
      const token = localStorage.getItem('token');
      if (!token || !endpoint) {
        scheduleReconnect();
        return;
      }

      activeController = new AbortController();

      try {
        const response = await fetch(`${endpoint}/notifications/sse`, {
          method: 'GET',
          headers: {
            authorization: token,
            Accept: 'text/event-stream',
          },
          signal: activeController.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE connection failed with status ${response.status}`);
        }

        retryDelay = 3000;
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (shouldReconnect) {
          const { value, done } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          let separatorIndex = buffer.indexOf('\n\n');
          while (separatorIndex >= 0) {
            const eventBlock = buffer.slice(0, separatorIndex).trim();
            buffer = buffer.slice(separatorIndex + 2);

            if (eventBlock && !eventBlock.startsWith(':')) {
              parseSseChunk(eventBlock, (eventName, eventData) => {
                try {
                  const payload = JSON.parse(eventData);

                  if (eventName === 'notification') {
                    window.dispatchEvent(new CustomEvent('novaNotificacaoSSE', { detail: payload }));
                    const handled = dispatchSseEvent(payload);

                    if (!handled) {
                      window.alert(`Nova notificação SSE: ${JSON.stringify(payload)}`);
                    }
                  }
                } catch (error) {
                  console.error('Erro ao processar evento SSE', error);
                }
              });
            }

            separatorIndex = buffer.indexOf('\n\n');
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Erro na conexão SSE:', error);
        }
      } finally {
        scheduleReconnect();
      }
    };

    connectToSse();

    return () => {
      shouldReconnect = false;
      if (activeController) {
        activeController.abort();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [endpoint]);
}
